import React from 'react';

import SortArrowsIcon from './SortArrowsIcon';
import {
  EmptyCell,
  EmptyRow,
  SortIcon,
  SortWrap,
  StyledBody,
  StyledCell,
  StyledHead,
  StyledHeaderCell,
  StyledRow,
  StyledTable,
  TableSize,
  TableWrap,
} from './styles';

export type TableProps = React.TableHTMLAttributes<HTMLTableElement> & {
  size?: TableSize;
  fullWidth?: boolean;
  striped?: boolean;
  bordered?: boolean;
  headerColor?: string;
  headerBackgroundColor?: string;
  borderColor?: string;
};

export type TableHeadProps = React.HTMLAttributes<HTMLTableSectionElement>;

export type TableBodyProps = React.HTMLAttributes<HTMLTableSectionElement> & {
  striped?: boolean;
};

export type TableRowProps = React.HTMLAttributes<HTMLTableRowElement> & {
  clickable?: boolean;
};

export type TableHeaderCellProps = React.ThHTMLAttributes<HTMLTableCellElement> & {
  align?: 'left' | 'center' | 'right';
  width?: string;
  sortable?: boolean;
  sortDirection?: 'asc' | 'desc' | null;
  onSort?: () => void;
  sortIcon?: React.ReactNode;
};

export type TableCellProps = React.TdHTMLAttributes<HTMLTableCellElement> & {
  align?: 'left' | 'center' | 'right';
  width?: string;
};

export type TableEmptyProps = {
  colSpan: number;
  children: React.ReactNode;
};

const TableRoot = ({
  size = 'md',
  fullWidth = true,
  striped = false,
  bordered = false,
  headerColor,
  headerBackgroundColor,
  borderColor,
  children,
  ...rest
}: TableProps): JSX.Element => {
  return (
    <TableWrap $fullWidth={fullWidth}>
      <StyledTable
        $size={size}
        $fullWidth={fullWidth}
        $striped={striped}
        $bordered={bordered}
        $headerColor={headerColor}
        $headerBackgroundColor={headerBackgroundColor}
        $borderColor={borderColor}
        {...rest}
      >
        {children}
      </StyledTable>
    </TableWrap>
  );
};

const Head = ({
  children,
  ...rest
}: TableHeadProps): JSX.Element => {
  return <StyledHead {...rest}>{children}</StyledHead>;
};

const Body = ({
  striped = false,
  children,
  ...rest
}: TableBodyProps): JSX.Element => {
  return (
    <StyledBody $striped={striped} {...rest}>
      {children}
    </StyledBody>
  );
};

const Row = ({
  clickable = false,
  children,
  ...rest
}: TableRowProps): JSX.Element => {
  return (
    <StyledRow $clickable={clickable} {...rest}>
      {children}
    </StyledRow>
  );
};

const HeaderCell = ({
  align = 'left',
  width,
  sortable = false,
  sortDirection = null,
  onSort,
  sortIcon,
  children,
  onClick,
  ...rest
}: TableHeaderCellProps): JSX.Element => {
  const handleClick = (event: React.MouseEvent<HTMLTableCellElement>): void => {
    onClick?.(event);
    if (sortable) onSort?.();
  };

  return (
    <StyledHeaderCell
      $align={align}
      $width={width}
      $sortable={sortable}
      onClick={handleClick}
      {...rest}
    >
      {sortable || sortIcon ? (
        <SortWrap>
          {children}
          {sortIcon || (
            <SortIcon aria-hidden="true">
              <SortArrowsIcon sortDirection={sortDirection} />
            </SortIcon>
          )}
        </SortWrap>
      ) : (
        children
      )}
    </StyledHeaderCell>
  );
};

const Cell = ({
  align = 'left',
  width,
  children,
  ...rest
}: TableCellProps): JSX.Element => {
  return (
    <StyledCell $align={align} $width={width} {...rest}>
      {children}
    </StyledCell>
  );
};

const Empty = ({ colSpan, children }: TableEmptyProps): JSX.Element => {
  return (
    <EmptyRow>
      <EmptyCell colSpan={colSpan}>{children}</EmptyCell>
    </EmptyRow>
  );
};

const Table = Object.assign(TableRoot, {
  Head,
  Body,
  Row,
  HeaderCell,
  Cell,
  Empty,
});

export default Table;
